/**
 * RouteGuard V5 - Identity-Based Auth
 * Simplified route protection component
 *
 * Key changes from V4:
 * - Removed user_role-based routing (user_role is now only user/super_admin)
 * - Merchant access determined by authentication status (web = merchant only)
 * - Server-side authorization via requireMerchant middleware handles actual access control
 */

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/useAuth';
import { PageLoading } from '@/components/PageLoading';
import type { NavigationState } from '@/types/infrastructure.types';

type RouteGuardNavigationState = NavigationState & {
	fromAuth?: boolean;
};

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Auth pages that authenticated users shouldn't access
 */
const AUTH_ROUTES = ['/merchant/login'];

/**
 * Default dashboard for authenticated users
 * Note: On web, only merchants can authenticate (customers use mobile app)
 */
const DEFAULT_DASHBOARD = '/merchant/dashboard';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * RouteGuard Component
 *
 * Protects routes based on authentication state.
 * Shows loading spinner during auth initialization.
 * Redirects unauthenticated users to login.
 *
 * Note: Role-based authorization happens server-side via requireMerchant middleware.
 * Frontend guards are for UX only - security is enforced on the server.
 *
 * @example
 * ```tsx
 * <RouteGuard>
 *   <MerchantDashboard />
 * </RouteGuard>
 * ```
 */
export const RouteGuard: React.FC<{
	children: React.ReactNode;
	redirectTo?: string;
}> = ({ children, redirectTo = '/merchant/login' }) => {
	const { session, loading: authLoading } = useAuth();
	const location = useLocation();

	const isAuthenticated = !!session;
	const isLoading = authLoading;

	// Get navigation state
	const navigationState = location.state as RouteGuardNavigationState | null;
	const isFromAuth = navigationState?.fromAuth === true;
	const isOnAuthPage = AUTH_ROUTES.includes(location.pathname);

	// Clear navigation state flags after render
	useEffect(() => {
		if (isFromAuth) {
			const timer = setTimeout(() => {
				window.history.replaceState({}, document.title);
			}, 0);
			return () => clearTimeout(timer);
		}
	}, [isFromAuth]);

	// Show loading spinner during auth initialization
	if (isLoading) {
		return <PageLoading message='Checking authentication...' />;
	}

	// Redirect authenticated users away from auth pages
	if (isAuthenticated && isOnAuthPage && !isFromAuth) {
		return <Navigate to={DEFAULT_DASHBOARD} replace />;
	}

	// Not authenticated - redirect to login
	if (!isAuthenticated) {
		const redirectState: NavigationState = {
			from: `${location.pathname}${location.search}${location.hash}`,
		};
		return <Navigate to={redirectTo} state={redirectState} replace />;
	}

	// Authenticated - render children
	return <>{children}</>;
};

// ============================================================================
// CONVENIENCE GUARDS
// ============================================================================

/**
 * Guard for merchant routes
 * Note: On web, authenticated = merchant (customers use mobile app)
 * Server-side requireMerchant middleware verifies merchant_id
 */
export const MerchantGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<RouteGuard redirectTo='/merchant/login'>{children}</RouteGuard>
);

/**
 * Guard for merchant admin-only routes
 * Note: Admin-specific restrictions are enforced server-side via requireMerchantRole('admin')
 * This guard only ensures authentication; actual role check happens on API calls
 */
export const MerchantAdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<RouteGuard redirectTo='/merchant/login'>{children}</RouteGuard>
);

// ============================================================================
// EXPORTS
// ============================================================================

export default RouteGuard;
